// ImportFromEmailModal — staff-facing booking-form helper.
//
// Findings from legacy investigation (parse_email.php + emails/get_emails.php
// + js/email-import.js):
//   - The legacy modal listed up-to-50 IMAP messages from the company
//     mailbox, then a "Import Selected" button POSTed `id=<imap_msgno>`
//     to process_email.php which combined the IMAP fetch + parser and
//     returned booking-shaped fields.
//   - Two senders are supported out of the box: ScrapCarComparison and
//     We Want Any Scrap. Both formats are documented in
//     server/src/modules/scrap/email-import/email-import.service.ts.
//
// Server flow (Phase 2):
//   - GET /import-email/inbox → live IMAP listing (or 404/503 banners).
//   - POST /import-email/parse with `{ email_uid, folder }` → fetches
//     the body server-side and runs the data-driven parser. Posting a
//     pasted `{ subject, body }` runs the parser without IMAP.
//
// Field population: when the server returns parsed fields, the modal
// fires `onApply(parsedFields)` and the parent booking form maps each
// field onto its state via the same setters the autocomplete uses.

import { useCallback, useEffect, useState } from 'react';
import { Download, Mail, RefreshCw, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import styles from './ImportFromEmailModal.module.css';

interface ApiErrorEnvelope {
  status: number;
  data?: {
    error?: { code?: string; message?: string; setup_url?: string };
  };
}

/**
 * Pull the {status, data} envelope off an axios-shaped error without
 * depending on axios.isAxiosError — vi.mock setups in this codebase
 * mock the apiClient module directly, so the rejected value's class
 * is whatever the test supplies. Detect by shape: `err.response.status`.
 */
function readApiError(err: unknown): ApiErrorEnvelope | null {
  if (
    err !== null &&
    typeof err === 'object' &&
    'response' in err &&
    (err as { response?: unknown }).response !== null &&
    typeof (err as { response?: unknown }).response === 'object'
  ) {
    const response = (err as { response: { status?: unknown; data?: unknown } })
      .response;
    if (typeof response.status === 'number') {
      return {
        status: response.status,
        data: response.data as ApiErrorEnvelope['data'],
      };
    }
  }
  return null;
}

const ALLOWED_FOLDERS = ['INBOX', 'Junk', 'Spam'] as const;
type AllowedFolder = (typeof ALLOWED_FOLDERS)[number];

interface InboxEmail {
  id: string;
  from: string;
  subject: string;
  date: string;
  preview: string;
}

interface InboxResponse {
  emails: InboxEmail[];
  reason?: 'not_configured' | 'connection_failed' | 'imap_pending' | 'ok';
  /** User-facing message for non-ok states; never the raw provider error. */
  message?: string;
  /** Server-supplied deep link to the settings page when not_configured. */
  setup_url?: string;
}

export interface ParsedBookingFields {
  source: string | null;
  // Canonical PHP-aligned fields — server populates these alongside the
  // legacy aliases below. Optional so older test payloads (legacy-only)
  // still satisfy the type.
  name?: string | null;
  car_make_model?: string | null;
  car_registration?: string | null;
  // Legacy aliases — written by the server's `applyLegacyAliases` so
  // existing callers keep working without coordination.
  customer_name: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  postcode: string | null;
  vehicle_reg: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_make_model: string | null;
  vehicle_colour: string | null;
  price: string | null;
  notes: string | null;
}

interface ParseResponse {
  parsed: ParsedBookingFields;
  recognised: boolean;
}

export interface ImportFromEmailModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (fields: ParsedBookingFields) => void;
}

export function ImportFromEmailModal({
  open,
  onClose,
  onApply,
}: ImportFromEmailModalProps): JSX.Element {
  const [inbox, setInbox] = useState<InboxResponse | null>(null);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [folder, setFolder] = useState<AllowedFolder>('INBOX');
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [pasteSubject, setPasteSubject] = useState('');
  const [pasteBody, setPasteBody] = useState('');
  // `parsing` covers the paste-body flow; `parsingInbox` covers the
  // parse-by-UID flow. Kept separate so the inbox button's spinner and
  // the footer button's spinner don't fight over a single flag.
  const [parsing, setParsing] = useState(false);
  const [parsingInbox, setParsingInbox] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const loadInbox = useCallback(async () => {
    setLoadingInbox(true);
    try {
      const res = await apiClient.get<InboxResponse>(
        endpoints.bookings.importEmailInbox,
        { params: { folder } },
      );
      setInbox(res.data ?? { emails: [] });
    } catch (err) {
      // 404 (not configured) and 503 (connection failed) are expected
      // states the modal renders as their own friendly banners — never
      // raw error text. Other errors fall through to a generic message.
      const apiErr = readApiError(err);
      if (apiErr !== null && apiErr.status === 404) {
        setInbox({
          emails: [],
          reason: 'not_configured',
          message:
            apiErr.data?.error?.message ??
            'Email import is not configured for this company. Set IMAP host, username, and password in Company Settings → Email & SMS → Booking Import.',
          setup_url:
            apiErr.data?.error?.setup_url ?? '/settings/company#email-import',
        });
      } else if (apiErr !== null && apiErr.status === 503) {
        setInbox({
          emails: [],
          reason: 'connection_failed',
          message:
            apiErr.data?.error?.message ??
            'Could not connect to the inbox. Check your IMAP host, port, and credentials, then try again.',
        });
      } else {
        setInbox({
          emails: [],
          reason: 'connection_failed',
          message:
            'Something went wrong loading your inbox. Please try again or contact support.',
        });
      }
    } finally {
      setLoadingInbox(false);
    }
  }, [folder]);

  useEffect(() => {
    if (!open) {
      // Drop selection when the modal closes so a fresh open never shows
      // a stale highlight on a row that may no longer exist.
      setSelectedUid(null);
      setParseError(null);
      return;
    }
    void loadInbox();
  }, [open, loadInbox]);

  function handleFolderChange(next: AllowedFolder) {
    // The inbox listing rebuilds on folder change — drop the selection
    // so a UID from the previous folder can't be parsed against the new
    // listing.
    setSelectedUid(null);
    setFolder(next);
  }

  async function handleParse() {
    if (pasteBody.trim().length === 0) {
      setParseError('Paste an email body to parse.');
      return;
    }
    setParseError(null);
    setParsing(true);
    try {
      const res = await apiClient.post<ParseResponse>(
        endpoints.bookings.importEmailParse,
        { subject: pasteSubject, body: pasteBody },
      );
      const data = res.data;
      if (!data) {
        setParseError('Server returned no data.');
        return;
      }
      if (!data.recognised) {
        setParseError(
          'Email format not recognised. Supported senders: ScrapCarComparison, We Want Any Scrap.',
        );
        return;
      }
      onApply(data.parsed);
      onClose();
      setPasteBody('');
      setPasteSubject('');
    } catch {
      setParseError('Could not parse email. Please try again.');
    } finally {
      setParsing(false);
    }
  }

  async function handleParseFromInbox() {
    if (selectedUid === null) return;
    setParseError(null);
    setParsingInbox(true);
    try {
      const res = await apiClient.post<ParseResponse>(
        endpoints.bookings.importEmailParse,
        { email_uid: selectedUid, folder },
      );
      const data = res.data;
      if (!data) {
        setParseError('Server returned no data.');
        return;
      }
      if (!data.recognised) {
        setParseError(
          'Email format not recognised. Supported senders: ScrapCarComparison, We Want Any Scrap.',
        );
        return;
      }
      onApply(data.parsed);
      onClose();
      setSelectedUid(null);
    } catch {
      setParseError('Could not parse email. Please try again.');
    } finally {
      setParsingInbox(false);
    }
  }

  const reason = inbox?.reason ?? 'ok';
  const hasInboxList = (inbox?.emails ?? []).length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import Booking from Email"
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={parsing || parsingInbox}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleParse()}
            loading={parsing}
            disabled={parsingInbox}
            icon={Search}
          >
            Parse pasted email
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              <Mail size={14} aria-hidden /> Inbox
            </h3>
            <label className={styles.folderSelect}>
              <span>Folder</span>
              <select
                value={folder}
                onChange={(e) => handleFolderChange(e.target.value as AllowedFolder)}
                aria-label="Inbox folder"
              >
                {ALLOWED_FOLDERS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loadingInbox ? (
            <div className={styles.muted}>Loading inbox…</div>
          ) : hasInboxList ? (
            <>
              <ul className={styles.inboxList} role="listbox" aria-label="Inbox messages">
                {(inbox?.emails ?? []).map((e) => {
                  const isSelected = e.id === selectedUid;
                  return (
                    <li key={e.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`${styles.inboxRow} ${isSelected ? styles.inboxRowSelected : ''}`}
                        onClick={() => setSelectedUid(e.id)}
                      >
                        <span className={styles.inboxFrom}>{e.from}</span>
                        <span className={styles.inboxSubject}>{e.subject}</span>
                        <span className={styles.inboxDate}>{e.date}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className={styles.inboxActions}>
                <Button
                  variant="primary"
                  size="sm"
                  icon={Download}
                  onClick={() => void handleParseFromInbox()}
                  loading={parsingInbox}
                  disabled={selectedUid === null || parsingInbox}
                >
                  Import Selected
                </Button>
              </div>
            </>
          ) : (
            <div className={styles.banner} role="status">
              {reason === 'not_configured' ? (
                <>
                  <strong>Email import is not configured</strong>
                  <p>
                    {inbox?.message ??
                      'Set IMAP host, username, and password in Company Settings → Email & SMS → Booking Import.'}
                  </p>
                  <p>
                    <Link
                      to={inbox?.setup_url ?? '/settings/company#email-import'}
                      onClick={onClose}
                      className={styles.setupLink}
                    >
                      Set up email import →
                    </Link>
                  </p>
                </>
              ) : reason === 'connection_failed' ? (
                <>
                  <strong>Could not connect to the inbox</strong>
                  <p>
                    {inbox?.message ??
                      'Check your IMAP host, port, and credentials, then try again.'}
                  </p>
                  <p>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={RefreshCw}
                      onClick={() => void loadInbox()}
                    >
                      Retry
                    </Button>
                  </p>
                </>
              ) : reason === 'imap_pending' ? (
                <>
                  <strong>Inbox listing is staged for the next release</strong>
                  <p>{inbox?.message ?? 'For now, paste an email body below — the parser still extracts the booking fields.'}</p>
                </>
              ) : (
                <p>{inbox?.message ?? 'No emails available.'}</p>
              )}
            </div>
          )}
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Paste email body</h3>
          <p className={styles.muted}>
            Paste the email subject + body. We support ScrapCarComparison and
            We Want Any Scrap formats.
          </p>
          <label className={styles.field}>
            <span>Subject</span>
            <input
              type="text"
              className={styles.input}
              value={pasteSubject}
              onChange={(e) => setPasteSubject(e.target.value)}
              aria-label="Email subject"
            />
          </label>
          <label className={styles.field}>
            <span>Body</span>
            <textarea
              rows={8}
              className={styles.textarea}
              value={pasteBody}
              onChange={(e) => setPasteBody(e.target.value)}
              aria-label="Email body"
            />
          </label>
          {parseError ? (
            <div className={styles.error} role="alert">
              {parseError}
            </div>
          ) : null}
        </section>
      </div>
    </Modal>
  );
}
